import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { postFacility, postDwelling, postRoom, postBed } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import SelectObject from "../../ui/SelectObject";
import IconButton from "../../ui/IconButton";
import states from "../../../data/states.json";

interface RoomDraft {
  name: string;
  bedCountText: string;
}

interface BulkPropertySetupFormState {
  kind: "facility" | "dwelling";
  existingNames: string[];
  dwellingTypes?: any[];
  onCreated: (propertyName: string) => void;
  onCancel: () => void;
  setErrMessage: any;
}

const MIN_ROOMS = 1;

const parseCount = (text: string) => {
  const parsed = parseInt(text, 10);
  return isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

const makeRoom = (index: number): RoomDraft => ({ name: `Room ${index + 1}`, bedCountText: "1" });

const BulkPropertySetupForm = ({
  kind,
  existingNames,
  dwellingTypes = [],
  onCreated,
  onCancel,
  setErrMessage,
}: BulkPropertySetupFormState) => {
  const label = kind === "facility" ? "Facility" : "Dwelling";
  const statesList = states;

  const getNextName = () => {
    const existing = new Set(existingNames);
    let n = 1;
    while (existing.has(`${label} ${n}`)) {
      n++;
    }
    return `${label} ${n}`;
  };

  const [name, setName] = useState<string>(() => getNextName());
  const [typeId, setTypeId] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [itemState, setItemState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [rooms, setRooms] = useState<RoomDraft[]>([makeRoom(0)]);
  const [roomCountText, setRoomCountText] = useState<string>("1");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRoomCountText(String(rooms.length));
  }, [rooms.length]);

  const markUnsaved = () => {
    unsavedChanges.value = true;
    setErrMessage(undefined);
  };

  const onUpdateName = (e: any) => {
    setName(e.target.value);
    markUnsaved();
  };
  const onUpdateType = (e: any) => {
    setTypeId(e.target.value);
    markUnsaved();
  };
  const onUpdateAddress = (e: any) => {
    setAddress(e.target.value);
    markUnsaved();
  };
  const onUpdateCity = (e: any) => {
    setCity(e.target.value);
    markUnsaved();
  };
  const onUpdateState = (e: any) => {
    setItemState(e.target.value);
    markUnsaved();
  };
  const onUpdateZip = (e: any) => {
    setZip(e.target.value);
    markUnsaved();
  };

  const setRoomCount = (count: number) => {
    const clamped = Math.max(MIN_ROOMS, count);
    setRooms((current) => {
      if (clamped === current.length) return current;
      if (clamped < current.length) return current.slice(0, clamped);
      const additions = Array.from({ length: clamped - current.length }, (_, i) => makeRoom(current.length + i));
      return [...current, ...additions];
    });
    markUnsaved();
  };

  // Let the user freely clear/retype the box (typing "1" -> "" -> "5" shouldn't
  // snap back to 1 mid-edit) - only clamp and rebuild the room list on blur.
  const onChangeRoomCountText = (e: any) => {
    setRoomCountText(e.target.value);
    markUnsaved();
  };

  const onBlurRoomCountText = () => {
    setRoomCount(parseCount(roomCountText) || MIN_ROOMS);
  };

  const onKeyDownRoomCountText = (e: any) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const onClickIncrementRooms = () => setRoomCount(rooms.length + 1);
  const onClickDecrementRooms = () => setRoomCount(rooms.length - 1);

  const onUpdateRoomName = (index: number, value: string) => {
    setRooms((current) => current.map((r, i) => (i === index ? { ...r, name: value } : r)));
    markUnsaved();
  };

  const onUpdateRoomBeds = (index: number, value: string) => {
    setRooms((current) => current.map((r, i) => (i === index ? { ...r, bedCountText: value } : r)));
    markUnsaved();
  };

  const onBlurRoomBeds = (index: number) => {
    setRooms((current) => current.map((r, i) => (i === index ? { ...r, bedCountText: String(parseCount(r.bedCountText)) } : r)));
  };

  const onClickAddRoom = (e: any) => {
    e.preventDefault();
    setRooms((current) => [...current, makeRoom(current.length)]);
    markUnsaved();
  };

  const onClickRemoveRoom = (index: number) => {
    setRooms((current) => current.filter((_, i) => i !== index));
    markUnsaved();
  };

  const totalBeds = rooms.reduce((sum, r) => sum + parseCount(r.bedCountText), 0);

  const onClickCreate = () => {
    if (!name) {
      setErrMessage(`Cannot set null ${label.toLowerCase()} name`);
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    setErrMessage("");
    setIsSaving(true);

    const propertyFields: any = { name };
    if (address) propertyFields.address_line_one = address;
    if (city) propertyFields.city = city;
    if (itemState) propertyFields.state = itemState;
    if (zip) propertyFields.zip = zip;
    if (kind === "dwelling" && typeId) {
      propertyFields.dwelling_type_id = typeId;
    }

    const createProperty = kind === "facility" ? postFacility(propertyFields) : postDwelling(propertyFields);

    createProperty
      .then((res: any) => {
        const propertyId = res[0]?.data?.property_id;
        return Promise.all(rooms.map((room) => postRoom({ name: room.name, property_id: propertyId })));
      })
      .then((roomResults: any) => {
        const bedCreates: Promise<any>[] = [];
        roomResults.forEach((roomRes: any, roomIndex: number) => {
          const roomId = roomRes[0]?.data?.room_id;
          const bedCount = parseCount(rooms[roomIndex].bedCountText);
          for (let b = 1; b <= bedCount; b++) {
            bedCreates.push(postBed({ room_id: roomId, name: `Bed ${b}` }));
          }
        });
        return Promise.all(bedCreates);
      })
      .then(() => {
        unsavedChanges.value = false;
        setIsSaving(false);
        onCreated(name);
      })
      .catch((err: any) => {
        setIsSaving(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({ top: 0, behavior: "auto" });
      });
  };

  const onClickCancel = () => {
    unsavedChanges.value = false;
    setErrMessage(undefined);
    onCancel();
  };

  return (
    <Card className="bg-light mb-1 mt-3">
      <Card.Body>
        <Row className="g-3">
          <Col xs={kind === "dwelling" ? 6 : 12}>
            <Input label="Name" type="text" value={name} onUpdate={onUpdateName} required={true} hasFocus={true} selectOnFocus={true} />
          </Col>
          {kind === "dwelling" && (
            <Col xs={12} md={6}>
              <SelectObject label="Type" value={typeId} options={dwellingTypes} onUpdate={onUpdateType} />
            </Col>
          )}
          <Col xs={12} md={6}>
            <Input label="Address" type="text" value={address} onUpdate={onUpdateAddress} />
          </Col>
          <Col xs={12} md={3}>
            <Input label="City" type="text" value={city} onUpdate={onUpdateCity} />
          </Col>
          <Col xs={6} md={3}>
            <SelectObject label="State" value={itemState} options={statesList} onUpdate={onUpdateState} />
          </Col>
          <Col xs={6} md={3}>
            <Input label="Zip" type="text" value={zip} onUpdate={onUpdateZip} />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12} md={5} lg={4}>
            <Form.Label className="text-muted small mb-1">Number of Rooms</Form.Label>
            <Stack direction="horizontal" gap={2}>
              <Button
                size="sm"
                className="app-secondary-color-inverse button"
                onClick={onClickDecrementRooms}
                disabled={rooms.length <= MIN_ROOMS}
              >
                <FontAwesomeIcon icon={faMinus} />
              </Button>
              <Form.Control
                type="number"
                className="form-control-enabled text-center"
                style={{ maxWidth: 72 }}
                value={roomCountText}
                onChange={onChangeRoomCountText}
                onBlur={onBlurRoomCountText}
                onKeyDown={onKeyDownRoomCountText}
              />
              <Button size="sm" className="app-secondary-color-inverse button" onClick={onClickIncrementRooms}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Stack>
          </Col>
        </Row>

        <Stack gap={2} className="mt-3">
          {rooms.map((room, index) => (
            <Stack key={index} direction="horizontal" gap={3} className="align-items-center">
              <Col>
                <Input label="Room Name" type="text" value={room.name} onUpdate={(e: any) => onUpdateRoomName(index, e.target.value)} />
              </Col>
              <Col xs={4} md={3} lg={2}>
                <Input
                  label="Number of Beds"
                  type="number"
                  value={room.bedCountText}
                  onUpdate={(e: any) => onUpdateRoomBeds(index, e.target.value)}
                  onBlur={() => onBlurRoomBeds(index)}
                />
              </Col>
              <IconButton
                onClickButton={() => onClickRemoveRoom(index)}
                tooltipText="Remove Room"
                icon={faTrash}
                disabled={rooms.length <= 1}
                tabIndex={-1}
              />
            </Stack>
          ))}
        </Stack>

        <a href="#blank" className="quick-link-on-card cursor-pointer ms-1 d-inline-block mt-2" onClick={onClickAddRoom}>
          + Add another room
        </a>

        <p className="text-muted small mt-3 mb-0">
          Will create {label.toLowerCase()} "{name || `New ${label}`}" with {rooms.length} room{rooms.length === 1 ? "" : "s"} and{" "}
          {totalBeds} bed{totalBeds === 1 ? "" : "s"}.
        </p>

        <Stack direction="horizontal" gap={3} className="mt-3">
          <Button className="app-secondary-color button" onClick={onClickCreate} disabled={isSaving}>
            Create {label}
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={onClickCancel} disabled={isSaving}>
            Cancel
          </Button>
        </Stack>
      </Card.Body>
    </Card>
  );
};

export default BulkPropertySetupForm;
